<template>
  <o-sheet class="pa-6 past-discussion-wrapper secondary--text" :class="[{ 'd-flex flex-column': !hasPastDiscussion }]">
    <div v-if="hasPastDiscussion" class="past-discussion-list">
      <div class="t-5 font-weight-bold d-flex" :class="{ 'mb-4': isMobile }">
        <div class="discussion-title">{{ $t(`discussion.index.title.pastDiscussion`) }}</div>
        <v-spacer></v-spacer>
        <o-btn variant="secondary" v-if="hasPastDiscussion" small @click="onViewAll">{{
          $t("captions.viewAll")
        }}</o-btn>
      </div>
      <div v-for="query in queryList" :key="query.queryId" class="past-discussion-container py-2 d-flex align-center">
        <v-avatar size="42">
          <img v-if="query.avatar" :src="query.avatar" alt="avat-img" />
          <!--Getting the Initials of the Learner, as the API doesn't return them-->
          <span v-else class="text--white white--text">
            {{ getInitials(query.displayName) }}
          </span>
        </v-avatar>
        <div class="d-flex ms-2 flex-grow-1">
          <div class="left-content flex-grow-1">
            <p class="mb-1">{{ query.displayTime }}</p>
            <!-- Ensure to keep RefId and Display Name in one line
          and Display Name to be dynamic, truncating as needed  -->
            <div class="d-flex align-center ref-learner-line">
              <p class="mb-0 ref-id">RefID: {{ query.queryId }}</p>
              <v-tooltip bottom v-if="isManager">
                <template v-slot:activator="{ on, attrs }">
                  <p class="mb-0 ms-2 learner-line" v-bind="attrs" v-on="on">
                    <span class="label">Learner:&nbsp;</span>
                    <span class="name">{{ query.displayName }}</span>
                  </p>
                </template>
                <span>{{ query.displayName }}</span>
              </v-tooltip>
            </div>
          </div>
          <div class="right-content">
            <o-btn variant="secondary" :to="`/discussion/${query.queryId}`" small outlined>{{
              $t("captions.viewRecap")
            }}</o-btn>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="mt-4 no-discussion-content flex-grow-1 d-flex justify-center align-center">
      <p v-if="!loading">
        {{
          isManager
            ? $t("discussion.index.caption.noPastDiscusssionPlm")
            : $t("discussion.index.caption.noPastDiscusssion")
        }}
      </p>
      <v-progress-circular v-else indeterminate size="64"></v-progress-circular>
    </div>
  </o-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import { QueryIndexFullDisplay, QueryService } from "@/service";
import OSheet from "@/framework/OSheet.vue";
import OBtn from "@/framework/OBtn.vue";

import { createHelpers } from "vuex-typescript-interface";
import { RootStoreInterface } from "@/store/types";

const { mapGetters } = createHelpers<RootStoreInterface>();

const getters = mapGetters(["user"]);

export default Vue.extend({
  components: {
    OSheet,
    OBtn,
  },
  data: () => ({
    queryList: [] as Array<QueryIndexFullDisplay>,
    loading: false,
  }),
  props: {
    userId: {
      type: [String, Number],
      required: true,
    },
    isExpert: {
      type: Boolean,
      required: true,
    },
    isManager: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    ...getters,
    hasPastDiscussion(): boolean {
      return this.queryList && this.queryList.length > 0;
    },
    isMobile(): boolean {
      // True when the viewport is under the breakpoint for mobile behavior
      return this.$vuetify.breakpoint.width <= 430;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      const currentTimeStamp = Date.now();

      const queryPayload = {
        pageSize: 3,
        sort: "meetingTime:desc",
        meetingEnd: currentTimeStamp,
      };
      try {
        const r = await QueryService.getQueryIndex(queryPayload);
        if (r.data && r.data.length) {
          this.queryList = r.data;
        }
      } catch (e) {
        console.log("error fetching past records:", e);
      }
      this.loading = false;
    },
    onViewAll() {
      this.$router
        .push({
          name: "Discussions",
          hash: this.isExpert ? "#requiresAction" : "#newOpportunities",
        })
        .catch(() => {
          /* Handle Error */
        });
    },
    show(query: QueryIndexFullDisplay) {
      this.$router
        .push({
          name: query.isInvited ? "Invitation" : "Discussion",
          params: { id: String(query.queryId) },
        })
        .catch(() => {
          /* Handle Error */
        });
    },
    getInitials(name: string): string {
      if (!name) return "";
      return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },
  },
  mounted() {
    this.load();
  },
});
</script>
<style lang="scss" scoped>
.v-avatar {
  background-color: var(--v-secondary-base);
}
.past-discussion-wrapper {
  height: 328px;
}

.past-discussion-container {
  border-bottom: 1px solid #dcdcdc;
}

.no-discussion-content p {
  max-width: 476px;
}

@media (max-width: 767px) {
  .past-discussion-wrapper {
    height: auto;
  }
}
.left-content {
  min-width: 0;
}
.past-discussion-container {
  overflow: hidden;
}
.d-flex.ms-2.flex-grow-1 {
  min-width: 0;
}
/* Truncates bassed on size  */
.ref-learner-line {
  flex-wrap: nowrap;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}
.ref-id {
  flex: 0 0 auto;
  white-space: nowrap;
}
.learner-line {
  display: flex;
  align-items: center;
  min-width: 0;
  margin: 0;
}
.learner-line .label {
  flex: 0 0 auto;
  white-space: nowrap;
}
.learner-line .name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.right-content {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: 12px;
}
</style>
